/**
 * ui/styles/tokens.ts
 *
 * Design tokens as CSS custom properties for consistent theming.
 * Color and shadow values inherit from document `--theme-*` tokens.
 *
 * Poster style: square corners, ink edges (`--border-width` thick on the
 * pieces that stand out), hard offset shadows, Archivo Black headings and
 * DM Mono for numbers and labels. The fonts are bundled (main.ts).
 */
import { css } from 'lit';

export const tokens = css`
  :host {
    user-select: none;
    -webkit-user-select: none;
    -webkit-touch-callout: none;
    touch-action: manipulation;

    /* Colors — inherit from document theme.css, with light fallbacks */
    --color-bg: var(--theme-color-bg, #ffffff);
    --color-surface: var(--theme-color-surface, #ffffff);
    --color-surface-hover: var(--theme-color-surface-hover, #f1f1f1);
    --color-muted-bg: var(--theme-color-muted-bg, #f1f1f1);
    /* Fill for the main action and selected states; text on it is --color-primary-text. */
    --color-primary: var(--theme-color-primary, #ffd23f);
    --color-primary-hover: var(--theme-color-primary-hover, #ffc61a);
    --color-primary-text: var(--theme-color-primary-text, #111111);
    --color-on-primary: var(--color-primary-text);
    --color-text: var(--theme-color-text, #111111);
    --color-text-secondary: var(--theme-color-text-secondary, #4a4a4a);
    --color-border: var(--theme-color-border, #111111);
    /* Ink outline and hard shadow color. */
    --color-edge: var(--theme-color-edge, #111111);
    --color-drop: var(--theme-color-drop, #111111);
    --color-danger: var(--theme-color-danger, #e5383b);
    --color-danger-hover: var(--theme-color-danger-hover, #c62828);
    --color-error: var(--color-danger);
    --color-success: var(--theme-color-success, #05865f);
    --color-warn: var(--theme-color-warn, #a35200);
    --color-pack: var(--theme-color-pack, #c2185b);
    --color-pack-bg: var(--theme-color-pack-bg, #ffd6e4);
    /* Readable on the page background: links, highlighted text, selected outlines. */
    --color-accent: var(--theme-color-link, #1c64d8);
    --color-accent-bg: var(--theme-color-accent-bg, rgba(255, 210, 63, 0.35));
    --color-overlay: var(--theme-color-overlay, rgba(17, 17, 17, 0.55));

    /* Poster blocks. Text on any of them is --color-on-block in both themes. */
    --color-yellow: var(--theme-color-yellow, #ffd23f);
    --color-pink: var(--theme-color-pink, #ff5d8f);
    --color-blue: var(--theme-color-blue, #3a86ff);
    --color-green: var(--theme-color-green, #06d6a0);
    --color-on-block: var(--theme-color-on-block, #111111);

    /* Spacing */
    --space-xs: 4px;
    --space-sm: 8px;
    --space-md: 16px;
    --space-lg: 24px;
    --space-xl: 32px;

    /* Typography */
    --font-family: 'Archivo', 'Helvetica Neue', Arial, sans-serif;
    --font-display: 'Archivo Black', 'Arial Black', Impact, sans-serif;
    --font-mono: 'DM Mono', ui-monospace, monospace;
    --font-size-sm: 0.875rem;
    --font-size-md: 1rem;
    --font-size-lg: 1.25rem;
    --font-size-xl: 1.5rem;
    --font-size-xxl: 2rem;
    --font-weight-normal: 400;
    --font-weight-medium: 600;
    --font-weight-bold: 800;

    /* Borders & Radius (square everywhere) */
    --border-width: 3px;
    --radius-sm: 0;
    --radius-md: 0;
    --radius-lg: 0;
    --radius-full: 0;

    /* Hard offset shadows */
    --shadow-sm: var(--theme-shadow-sm, 2px 2px 0 #111111);
    --shadow-md: var(--theme-shadow-md, 4px 4px 0 #111111);
    --shadow-lg: var(--theme-shadow-lg, 8px 8px 0 #111111);

    /* Transitions — none with the game's Reduced motion setting (theme.css) */
    --transition-fast: var(--theme-transition-fast, 80ms ease);
    --transition-normal: var(--theme-transition-normal, 250ms ease);

    /* Z-index layers */
    --z-toolbar: 100;
    --z-modal-backdrop: 200;
    --z-modal: 210;
    --z-toast: 300;

    font-family: var(--font-family);
  }

  h1,
  h2,
  h3 {
    font-family: var(--font-display);
    font-weight: 400;
    font-synthesis: none;
    text-transform: uppercase;
    letter-spacing: -0.01em;
    line-height: 1.05;
  }

  :focus-visible {
    outline: 3px solid var(--color-blue);
    outline-offset: 2px;
  }
`;
